/**
 * ResultsCard Component
 * Displays eligibility classification, confidence, rationale, and sources
 * With beautiful OG preview for URL searches
 */

'use client';

import ConfidenceBadge from './ConfidenceBadge';
import SourcesList from './SourcesList';
import FavoriteButton from './FavoriteButton';
import { Classification, Requirement, Source, OpenGraphData } from '@/lib/validators';

interface ResultsCardProps {
  query: string;
  classification: Classification;
  requires?: Requirement;
  confidence: number;
  rationale: string;
  sources: Source[];
  notes?: string;
  ogData?: OpenGraphData | null;
  ogLoading?: boolean;
  isUrl?: boolean;
}

export default function ResultsCard({
  query,
  classification,
  requires,
  confidence,
  rationale,
  sources,
  notes,
  ogData,
  ogLoading,
  isUrl,
}: ResultsCardProps) {
  // Extract clean URL from query
  const getCleanUrl = () => {
    const cleanQ = query.startsWith('url:') ? query.slice(4) : query;
    if (!cleanQ.startsWith('http://') && !cleanQ.startsWith('https://')) {
      return `https://${cleanQ}`;
    }
    return cleanQ;
  };

  // Get display name for the product
  const getDisplayName = () => {
    if (ogData?.title) {
      return ogData.title;
    }
    if (isUrl) {
      try {
        const url = new URL(getCleanUrl());
        return url.hostname.replace('www.', '');
      } catch {
        return query;
      }
    }
    return query;
  };
  const getClassificationColor = () => {
    switch (classification) {
      case 'YES':
        return 'chip-yes';
      case 'NO':
        return 'chip-no';
      case 'SOMETIMES':
        return 'chip-sometimes';
      default:
        return 'chip-sometimes';
    }
  };

  const getClassificationIcon = () => {
    switch (classification) {
      case 'YES':
        return '✓';
      case 'NO':
        return '✗';
      case 'SOMETIMES':
        return '⚠';
      default:
        return '?';
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-8 py-6 border-b border-primary-200">
        {/* URL Preview Card */}
        {isUrl && (
          <div className="mb-4">
            {ogLoading ? (
              <div className="flex items-center gap-4 p-4 bg-white/70 rounded-xl border border-primary-200 animate-pulse">
                <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ) : ogData ? (
              <a
                href={getCleanUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-4 bg-white/80 hover:bg-white rounded-xl border border-primary-200 hover:border-primary-300 transition-all group"
              >
                {/* Product Image */}
                {ogData.image?.url ? (
                  <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                    <img
                      src={ogData.image.url}
                      alt={ogData.title || 'Product'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 flex-shrink-0 rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center border border-primary-200">
                    <svg className="w-8 h-8 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                )}

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-lg leading-tight group-hover:text-primary-600 transition-colors line-clamp-2">
                    {ogData.title || 'Product Page'}
                  </h3>
                  {ogData.description && (
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                      {ogData.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    {ogData.favicon && (
                      <img
                        src={ogData.favicon}
                        alt=""
                        className="w-4 h-4 rounded"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    <span className="text-xs text-gray-500 truncate">
                      {ogData.siteName || new URL(getCleanUrl()).hostname.replace('www.', '')}
                    </span>
                    <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                </div>
              </a>
            ) : (
              <a
                href={getCleanUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-white/80 hover:bg-white rounded-xl border border-primary-200 hover:border-primary-300 transition-all group"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <span className="text-sm text-gray-600 truncate flex-1 group-hover:text-primary-600 transition-colors">
                  {getCleanUrl()}
                </span>
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
          </div>
        )}

        <div className="flex items-start justify-between gap-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Eligibility for: <span className="text-primary-600">{isUrl ? getDisplayName() : query}</span>
          </h2>
          
          {/* Save to Favorites Button */}
          <FavoriteButton
            itemId={query}
            title={isUrl ? getDisplayName() : query}
            description={rationale.substring(0, 100)}
            image={ogData?.image?.url || undefined}
            classification={classification}
            showLabel
            className="flex-shrink-0 px-4 py-2 rounded-xl border-2 border-gray-200 bg-white hover:border-red-200 hover:bg-red-50"
          />
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Classification Chip */}
          <span
            className={`${getClassificationColor()} inline-flex items-center px-4 py-2 rounded-full text-sm font-bold border`}
          >
            <span className="mr-2 text-lg">{getClassificationIcon()}</span>
            {classification}
          </span>

          {/* Requirement Badge */}
          {requires && requires !== 'None' && (
            <span className="bg-blue-100 text-blue-800 border-blue-300 inline-flex items-center px-4 py-2 rounded-full text-sm font-bold border">
              Requires: {requires}
            </span>
          )}

          {/* Confidence Badge */}
          <ConfidenceBadge confidence={confidence} />
        </div>
      </div>

      {/* Body */}
      <div className="px-8 py-6 space-y-6">
        {/* Rationale */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Rationale</h3>
          <p className="text-gray-700 leading-relaxed">{rationale}</p>
        </div>

        {/* Sources */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Sources</h3>
          <SourcesList sources={sources} />
        </div>

        {/* Additional Notes */}
        {notes && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h4 className="font-semibold text-amber-900 mb-2 flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              Important Note
            </h4>
            <p className="text-amber-800 text-sm">{notes}</p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
          <h4 className="font-bold text-red-900 mb-2">⚠️ Important Disclaimer</h4>
          <p className="text-red-800 text-sm leading-relaxed">
            This information is for educational purposes only and should not be considered medical,
            tax, or financial advice. FSA/HSA eligibility rules can vary by plan administrator and
            may change over time. <strong>Always verify with your specific plan administrator</strong>{' '}
            before making purchases. This is not a substitute for professional medical or tax advice.
            We are not responsible for any reimbursement denials or tax consequences.
          </p>
        </div>
      </div>
    </div>
  );
}

